<template>
  <div class="p-6">
    <div v-if="!checkCurrentSubscription">
      <form @submit.prevent="submitBilling" v-if="authorized || vaultHasNoSubscription">
        <div class="border p-8">
          <div v-if="vaultHasNoSubscription" class="mb-6 pb-4 border-b border-grey">
            <p class="text-grey-dark font-medium mb-2">
              <i class="fa fa-lock mr-2"></i>This Vault is locked
            </p>
            <p class="text-grey-dark text-sm">
              Add your payment details below. To complete unlocking this Vault, contact support.
            </p>
          </div>

          <div v-if="vaultHasNoSubscription" class="flex flex-wrap mt-4 mb-4">
            <div class="w-full pb-4">
              <label class="font-medium">
                <span class="text-red">*</span> Plan
              </label>
              <select
                v-model.number="selectedPlanForLockedVaultIndex"
                class="w-full border py-2 px-3 mt-2 rounded max-w-md"
                :class="{ 'border-red': errors.has('plan') }"
                name="plan"
              >
                <option :value="null" disabled>Select a plan</option>
                <option
                  v-for="(plan, index) in availablePlansForLockedVault"
                  :key="index"
                  :value="index"
                >
                  {{ plan.name }} – {{ plan.interval }} - {{ plan.storage_megabytes }} MB - {{ plan.price }} {{ plan.currency }} 
                </option>
              </select>
              <div v-show="errors.has('plan')" class="text-red text-sm mt-2">
                {{ errors.first("plan") }}
              </div>
            </div>
          </div>

          <div
            v-if="billing.type == 'direct-debit'"
            class="flex justify-center items-center"
          >
            <p class="uppercase text-lg font-light text-center">
              Direct Debit Request
            </p>
          </div>
          <div class="flex flex-wrap relative">
            <div class="w-full pr-8">
              <div class="flex justify-between">
                <label class="font-medium">
                  <span class="text-red">*</span> Type
                </label>
                <label class="text-red text-sm font-medium"
                  >* Required info</label
                >
              </div>
              <ul class="flex flex-col sm:flex-row">
                <li v-for="(type, index) in types" :key="index">
                  <label class="inline-block py-4 pr-4">
                    <input
                      class="mr-2"
                      name="type"
                      type="radio"
                      v-model="billing.type"
                      :value="type.code"
                    />
                    {{ type.name }}
                  </label>
                </li>
              </ul>
              <div v-show="errors.has('type')" class="text-red text-sm mt-2">
                {{ errors.first("type") }}
              </div>
            </div>
          </div>

          <div class="flex flex-wrap mt-4">
            <!-- credit card -->
            <div v-if="billing.type == 'credit-card'" class="w-full">
              <div class="flex flex-col">
                <label class="font-medium text-sm">We accept</label>
                <div>
                  <svg
                    class="w-10 h-10"
                    enable-background="new 0 0 780 500"
                    version="1.1"
                    viewBox="0 0 780 500"
                    xml:space="preserve"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M40,0h700c22.092,0,40,17.909,40,40v420c0,22.092-17.908,40-40,40H40c-22.091,0-40-17.908-40-40V40   C0,17.909,17.909,0,40,0z"
                      fill="#0E4595"
                    />
                    <path
                      d="m293.2 348.73l33.361-195.76h53.36l-33.385 195.76h-53.336zm246.11-191.54c-10.57-3.966-27.137-8.222-47.822-8.222-52.725 0-89.865 26.55-90.18 64.603-0.299 28.13 26.514 43.822 46.752 53.186 20.771 9.595 27.752 15.714 27.654 24.283-0.131 13.121-16.586 19.116-31.922 19.116-21.357 0-32.703-2.967-50.227-10.276l-6.876-3.11-7.489 43.823c12.463 5.464 35.51 10.198 59.438 10.443 56.09 0 92.5-26.246 92.916-66.882 0.199-22.269-14.016-39.216-44.801-53.188-18.65-9.055-30.072-15.099-29.951-24.268 0-8.137 9.668-16.839 30.557-16.839 17.449-0.27 30.09 3.535 39.938 7.5l4.781 2.26 7.232-42.429m137.31-4.223h-41.232c-12.773 0-22.332 3.487-27.941 16.234l-79.244 179.4h56.031s9.16-24.123 11.232-29.418c6.125 0 60.555 0.084 68.338 0.084 1.596 6.853 6.49 29.334 6.49 29.334h49.514l-43.188-195.64zm-65.418 126.41c4.412-11.279 21.26-54.723 21.26-54.723-0.316 0.522 4.379-11.334 7.074-18.684l3.605 16.879s10.219 46.729 12.354 56.528h-44.293zm-363.3-126.41l-52.24 133.5-5.567-27.13c-9.725-31.273-40.025-65.155-73.898-82.118l47.766 171.2 56.456-0.064 84.004-195.39h-56.521"
                      fill="#fff"
                    />
                    <path
                      d="m146.92 152.96h-86.041l-0.681 4.073c66.938 16.204 111.23 55.363 129.62 102.41l-18.71-89.96c-3.23-12.395-12.597-16.094-24.186-16.527"
                      fill="#F2AE14"
                    />
                  </svg>
                  <svg
                    class="w-10 h-10 ml-2"
                    enable-background="new 0 0 780 500"
                    version="1.1"
                    viewBox="0 0 780 500"
                    xml:space="preserve"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M40,0h700c22.092,0,40,17.909,40,40v420c0,22.092-17.908,40-40,40H40c-22.091,0-40-17.908-40-40V40   C0,17.909,17.909,0,40,0z"
                      fill="#16366F"
                    />
                    <path
                      d="m449.01 250c0 99.143-80.37 179.5-179.51 179.5s-179.5-80.361-179.5-179.5c0-99.133 80.362-179.5 179.5-179.5 99.137 0 179.51 80.37 179.51 179.5"
                      fill="#D9222A"
                    />
                    <path
                      d="m510.49 70.496c-46.38 0-88.643 17.596-120.5 46.466-6.49 5.889-12.548 12.237-18.125 18.996h36.266c4.966 6.037 9.536 12.388 13.685 19.013h-63.635c-3.827 6.121-7.28 12.469-10.341 19.008h84.312c2.893 6.185 5.431 12.53 7.6 19.004h-99.512c-2.091 6.235-3.832 12.581-5.217 19.009h109.94c2.689 12.49 4.044 25.231 4.041 38.008 0 19.934-3.254 39.113-9.254 57.02h-99.512c2.164 6.479 4.7 12.825 7.595 19.01h84.317c-3.064 6.54-6.52 12.889-10.347 19.013h-63.625c4.154 6.629 8.73 12.979 13.685 18.996h36.258c-5.57 6.772-11.63 13.126-18.13 19.012 31.86 28.867 74.118 46.454 120.5 46.454 99.138-1e-3 179.51-80.362 179.51-179.5 0-99.13-80.37-179.5-179.51-179.5"
                      fill="#EE9F2D"
                    />
                    <path
                      d="m666.08 350.06c0-3.201 2.592-5.801 5.796-5.801s5.796 2.6 5.796 5.801c0 3.199-2.592 5.799-5.796 5.799-3.202-1e-3 -5.797-2.598-5.796-5.799zm5.796 4.408c2.435-1e-3 4.407-1.975 4.408-4.408 0-2.433-1.972-4.404-4.404-4.404h-4e-3c-2.429-4e-3 -4.4 1.963-4.404 4.392v0.013c-3e-3 2.432 1.967 4.406 4.399 4.408 1e-3 -1e-3 3e-3 -1e-3 5e-3 -1e-3zm-0.783-1.86h-1.188v-5.094h2.149c0.45 0 0.908 0 1.305 0.254 0.413 0.278 0.646 0.77 0.646 1.278 0 0.57-0.337 1.104-0.883 1.312l0.937 2.25h-1.315l-0.78-2.016h-0.87v2.016h-1e-3zm0-2.89h0.658c0.246 0 0.504 0.02 0.725-0.1 0.196-0.125 0.296-0.359 0.296-0.584 0-0.195-0.12-0.42-0.288-0.516-0.207-0.131-0.536-0.101-0.758-0.101h-0.633v1.301zm-443.5-80.063c-2.045-0.237-2.945-0.301-4.35-0.301-11.045 0-16.637 3.789-16.637 11.268 0 4.611 2.73 7.546 6.987 7.546 7.938 0 13.659-7.56 14-18.513zm14.171 32.996h-16.146l0.371-7.676c-4.925 6.067-11.496 8.95-20.425 8.95-10.562 0-17.804-8.25-17.804-20.229 0-18.024 12.596-28.54 34.217-28.54 2.208 0 5.041 0.2 7.941 0.569 0.605-2.441 0.763-3.486 0.763-4.8 0-4.908-3.396-6.738-12.5-6.738-9.533-0.108-17.396 2.271-20.625 3.334 0.204-1.23 2.7-16.658 2.7-16.658 9.712-2.846 16.117-3.917 23.325-3.917 16.733 0 25.596 7.512 25.58 21.712 0.032 3.805-0.597 8.5-1.58 14.671-1.692 10.731-5.32 33.718-5.817 39.322zm-62.158 0h-19.488l11.163-69.997-24.925 69.997h-13.28l-1.64-69.597-11.734 69.597h-18.242l15.238-91.054h28.02l1.7 50.966 17.092-50.966h31.167l-15.071 91.054m354.98-32.996c-2.037-0.237-2.942-0.301-4.342-0.301-11.041 0-16.634 3.789-16.634 11.268 0 4.611 2.726 7.546 6.983 7.546 7.939 0 13.664-7.56 13.993-18.513zm14.183 32.996h-16.145l0.365-7.676c-4.925 6.067-11.5 8.95-20.42 8.95-10.566 0-17.8-8.25-17.8-20.229 0-18.024 12.587-28.54 34.212-28.54 2.208 0 5.037 0.2 7.934 0.569 0.604-2.441 0.763-3.486 0.763-4.8 0-4.908-3.392-6.738-12.496-6.738-9.533-0.108-17.388 2.271-20.63 3.334 0.205-1.23 2.709-16.658 2.709-16.658 9.713-2.846 16.113-3.917 23.312-3.917 16.741 0 25.604 7.512 25.588 21.712 0.032 3.805-0.597 8.5-1.58 14.671-1.682 10.731-5.32 33.718-5.812 39.322zm-220.39-1.125c-5.334 1.68-9.492 2.399-14 2.399-9.963 0-15.4-5.725-15.4-16.267-0.142-3.27 1.433-11.879 2.67-19.737 1.125-6.917 8.45-50.53 8.45-50.53h19.371l-2.262 11.209h11.7l-2.643 17.796h-11.742c-2.25 14.083-5.454 31.625-5.491 33.95 0 3.817 2.037 5.483 6.67 5.483 2.221 0 3.941-0.226 5.255-0.7l-2.578 16.397m59.391-0.6c-6.654 2.033-13.075 3.017-19.879 3-21.683-0.021-32.987-11.346-32.987-33.032 0-25.313 14.38-43.947 33.9-43.947 15.97 0 26.17 10.433 26.17 26.796 0 5.429-0.7 10.729-2.387 18.212h-38.575c-1.304 10.742 5.57 15.217 16.837 15.217 6.935 0 13.188-1.43 20.142-4.663l-3.221 18.417zm-10.887-43.9c0.107-1.543 2.054-13.217-9.013-13.217-6.171 0-10.583 4.704-12.38 13.217h21.393zm-123.42-5.017c0 9.367 4.541 15.825 14.841 20.676 7.892 3.709 9.113 4.809 9.113 8.17 0 4.617-3.48 6.7-11.192 6.7-5.812 0-11.22-0.907-17.458-2.92 0 0-2.563 16.32-2.68 17.101 4.43 0.966 8.38 1.861 20.28 2.19 20.562 0 30.058-7.829 30.058-24.75 0-10.175-3.975-16.146-13.737-20.633-8.171-3.75-9.109-4.588-9.109-8.046 0-4.004 3.238-6.046 9.538-6.046 3.825 0 9.05 0.408 14 1.113l2.775-17.175c-5.046-0.8-12.696-1.442-17.15-1.442-21.8 0-29.346 11.387-29.279 25.062m229.09-23.116c5.413 0 10.459 1.42 17.413 4.92l3.187-19.762c-2.854-1.12-12.904-7.7-21.416-7.7-13.042 0-24.066 6.47-31.82 17.15-11.31-3.746-15.959 3.825-21.659 11.367l-5.062 1.179c0.383-2.483 0.73-4.95 0.613-7.446h-17.896c-2.445 22.917-6.779 46.13-10.171 69.075l-0.884 4.976h19.496c3.254-21.143 5.038-34.681 6.121-43.842l7.342-4.084c1.096-4.08 4.529-5.458 11.416-5.292-0.926 5.008-1.389 10.09-1.383 15.184 0 24.225 13.071 39.308 34.05 39.308 5.404 0 10.042-0.712 17.221-2.657l3.431-20.76c-6.46 3.18-11.761 4.676-16.561 4.676-11.328 0-18.183-8.362-18.183-22.184-1e-3 -20.05 10.195-34.108 24.745-34.108"
                    />
                    <path
                      d="m185.21 297.24h-19.491l11.17-69.988-24.925 69.988h-13.282l-1.642-69.588-11.733 69.588h-18.243l15.238-91.042h28.02l0.788 56.362 18.904-56.362h30.267l-15.071 91.042"
                      fill="#fff"
                    />
                    <path
                      d="m647.52 211.6l-4.319 26.308c-5.33-7.012-11.054-12.087-18.612-12.087-9.834 0-18.784 7.454-24.642 18.425-8.158-1.692-16.597-4.563-16.597-4.563l-4e-3 0.067c0.658-6.133 0.92-9.875 0.862-11.146h-17.9c-2.437 22.917-6.77 46.13-10.157 69.075l-0.893 4.976h19.492c2.633-17.097 4.65-31.293 6.133-42.551 6.659-6.017 9.992-11.267 16.721-10.917-2.979 7.206-4.725 15.504-4.725 24.017 0 18.513 9.367 30.725 23.534 30.725 7.141 0 12.62-2.462 17.966-8.17l-0.912 6.884h18.433l14.842-91.043h-19.222zm-24.37 73.942c-6.634 0-9.983-4.909-9.983-14.597 0-14.553 6.271-24.875 15.112-24.875 6.695 0 10.32 5.104 10.32 14.508 1e-3 14.681-6.369 24.964-15.449 24.964z"
                    />
                    <path
                      d="m233.19 264.26c-2.042-0.236-2.946-0.3-4.346-0.3-11.046 0-16.634 3.788-16.634 11.267 0 4.604 2.73 7.547 6.98 7.547 7.945-1e-3 13.666-7.559 14-18.514zm14.179 32.984h-16.146l0.367-7.663c-4.921 6.054-11.5 8.95-20.421 8.95-10.567 0-17.804-8.25-17.804-20.229 0-18.032 12.591-28.542 34.216-28.542 2.209 0 5.042 0.2 7.938 0.571 0.604-2.442 0.762-3.487 0.762-4.808 0-4.908-3.391-6.73-12.496-6.73-9.537-0.108-17.395 2.272-20.629 3.322 0.204-1.226 2.7-16.638 2.7-16.638 9.709-2.858 16.121-3.93 23.321-3.93 16.738 0 25.604 7.518 25.588 21.705 0.029 3.82-0.605 8.512-1.584 14.675-1.687 10.725-5.32 33.725-5.812 39.317zm261.38-88.592l-3.192 19.767c-6.95-3.496-12-4.921-17.407-4.921-14.551 0-24.75 14.058-24.75 34.107 0 13.821 6.857 22.181 18.183 22.181 4.8 0 10.096-1.492 16.554-4.677l-3.42 20.75c-7.184 1.959-11.816 2.672-17.226 2.672-20.976 0-34.05-15.084-34.05-39.309 0-32.55 18.059-55.3 43.888-55.3 8.507 1e-3 18.562 3.609 21.42 4.73m31.442 55.608c-2.041-0.236-2.941-0.3-4.346-0.3-11.042 0-16.634 3.788-16.634 11.267 0 4.604 2.729 7.547 6.984 7.547 7.937-1e-3 13.662-7.559 13.996-18.514zm14.179 32.984h-16.15l0.37-7.663c-4.924 6.054-11.5 8.95-20.42 8.95-10.563 0-17.804-8.25-17.804-20.229 0-18.032 12.595-28.542 34.212-28.542 2.213 0 5.042 0.2 7.941 0.571 0.601-2.442 0.763-3.487 0.763-4.808 0-4.908-3.392-6.73-12.496-6.73-9.533-0.108-17.396 2.272-20.629 3.322 0.204-1.226 2.704-16.638 2.704-16.638 9.709-2.858 16.116-3.93 23.316-3.93 16.742 0 25.604 7.518 25.583 21.705 0.034 3.82-0.595 8.512-1.579 14.675-1.682 10.725-5.324 33.725-5.811 39.317zm-220.39-1.122c-5.338 1.68-9.496 2.409-14 2.409-9.963 0-15.4-5.726-15.4-16.266-0.138-3.281 1.437-11.881 2.675-19.738 1.12-6.926 8.446-50.533 8.446-50.533h19.367l-2.259 11.212h9.942l-2.646 17.788h-9.975c-2.25 14.091-5.463 31.619-5.496 33.949 0 3.83 2.042 5.483 6.671 5.483 2.22 0 3.938-0.217 5.254-0.692l-2.579 16.388m59.392-0.591c-6.65 2.033-13.08 3.013-19.88 3-21.684-0.021-32.987-11.346-32.987-33.033 0-25.321 14.38-43.95 33.9-43.95 15.97 0 26.17 10.429 26.17 26.8 0 5.433-0.7 10.733-2.382 18.212h-38.575c-1.306 10.741 5.569 15.221 16.837 15.221 6.93 0 13.188-1.434 20.137-4.676l-3.22 18.426zm-10.892-43.912c0.117-1.538 2.059-13.217-9.013-13.217-6.166 0-10.579 4.717-12.375 13.217h21.388zm-123.42-5.004c0 9.365 4.542 15.816 14.842 20.675 7.891 3.708 9.112 4.812 9.112 8.17 0 4.617-3.483 6.7-11.187 6.7-5.817 0-11.225-0.908-17.467-2.92 0 0-2.554 16.32-2.67 17.1 4.42 0.967 8.374 1.85 20.274 2.191 20.567 0 30.059-7.829 30.059-24.746 0-10.18-3.971-16.15-13.738-20.637-8.167-3.758-9.112-4.583-9.112-8.046 0-4 3.245-6.058 9.541-6.058 3.821 0 9.046 0.42 14.004 1.125l2.771-17.18c-5.041-0.8-12.691-1.441-17.146-1.441-21.804 0-29.345 11.379-29.283 25.067m398.45 50.629h-18.437l0.917-6.893c-5.347 5.717-10.825 8.18-17.967 8.18-14.168 0-23.53-12.213-23.53-30.725 0-24.63 14.521-45.393 31.709-45.393 7.558 0 13.28 3.088 18.604 10.096l4.325-26.308h19.221l-14.842 91.043zm-28.745-17.109c9.075 0 15.45-10.283 15.45-24.953 0-9.405-3.63-14.509-10.325-14.509-8.838 0-15.116 10.317-15.116 24.875-1e-3 9.686 3.357 14.587 9.991 14.587zm-56.843-56.929c-2.439 22.917-6.773 46.13-10.162 69.063l-0.891 4.975h19.491c6.971-45.275 8.658-54.117 19.588-53.009 1.742-9.266 4.982-17.383 7.399-21.479-8.163-1.7-12.721 2.913-18.688 11.675 0.471-3.787 1.334-7.466 1.163-11.225h-17.9m-160.42 0c-2.446 22.917-6.78 46.13-10.167 69.063l-0.887 4.975h19.5c6.962-45.275 8.646-54.117 19.569-53.009 1.75-9.266 4.992-17.383 7.4-21.479-8.154-1.7-12.716 2.913-18.678 11.675 0.47-3.787 1.325-7.466 1.162-11.225h-17.899m254.57 68.242c0-3.214 2.596-5.8 5.796-5.8 3.197-3e-3 5.792 2.587 5.795 5.785v0.015c-1e-3 3.2-2.595 5.794-5.795 5.796-3.2-2e-3 -5.794-2.596-5.796-5.796zm5.796 4.404c2.432 1e-3 4.403-1.97 4.403-4.401v-2e-3c3e-3 -2.433-1.968-4.406-4.399-4.408h-4e-3c-2.435 1e-3 -4.408 1.974-4.409 4.408 3e-3 2.432 1.976 4.403 4.409 4.403zm-0.784-1.87h-1.188v-5.084h2.154c0.446 0 0.908 8e-3 1.296 0.254 0.416 0.283 0.654 0.767 0.654 1.274 0 0.575-0.338 1.113-0.888 1.317l0.941 2.236h-1.319l-0.78-2.008h-0.87v2.008 3e-3zm0-2.88h0.654c0.245 0 0.513 0.018 0.729-0.1 0.195-0.125 0.295-0.361 0.295-0.587-9e-3 -0.21-0.115-0.404-0.287-0.524-0.204-0.117-0.542-0.085-0.763-0.085h-0.629v1.296h1e-3z"
                      fill="#fff"
                    />
                  </svg>
                </div>
              </div>
              <div class="w-full flex flex-col sm:flex-row">
                <div class="w-full md:w-1/2 py-4 md:pr-8">
                  <label class="font-medium">Name on Card</label>
                  <input
                    tabindex="1"
                    type="text"
                    class="w-full border py-2 px-3 mt-2 rounded"
                    v-model="billing.account_name"
                    v-validate="'max:32|min:3'"
                    key="name_on_card"
                    name="name_on_card"
                    :class="{ 'border-red': errors.has('name_on_card') }"
                  />
                  <div
                    v-show="errors.has('name_on_card')"
                    class="text-red text-sm mt-2"
                  >
                    {{ errors.first("name_on_card") }}
                  </div>
                </div>
                <div class="w-full md:w-1/2 py-4">
                  <label class="font-medium">Card Number </label>
                  <input
                    tabindex="2"
                    type="text"
                    class="w-full border py-2 px-3 mt-2 rounded"
                    v-model="billing.account_number"
                    v-validate="
                      `${
                        billing.account_name ? 'required' : ''
                      }|visa_or_mastercard:${cardBrand}`
                    "
                    key="credit_card_account_number"
                    name="credit_card_account_number"
                    :class="{
                      'border-red': errors.has('credit_card_account_number'),
                    }"
                    v-cardformat:formatCardNumber
                  />
                  <div
                    v-show="errors.has('credit_card_account_number')"
                    class="text-red text-sm mt-2"
                  >
                    {{ errors.first("credit_card_account_number") }}
                  </div>
                </div>
              </div>
              <div class="w-full flex flex-col sm:flex-row">
                <div class="sm:w-full py-4">
                  <label class="font-medium"> Card Expiration </label>
                  <div class="w-full">
                    <masked-input
                      tabindex="3"
                      v-validate="`${billing.account_number ? 'required' : ''}`"
                      mask="11/11"
                      placeholder="MM  /  YY"
                      name="card_expiry"
                      type="text"
                      key="card_expiry"
                      v-model="billing.card_expiry"
                      class="
                        sm:w-48
                        w-full
                        text-center
                        border
                        py-2
                        px-3
                        mt-2
                        rounded
                      "
                      :class="{ 'border-red': errors.has('card_expiry') }"
                    />
                  </div>
                  <div
                    v-show="errors.has('card_expiry')"
                    class="text-red text-sm mt-2"
                  >
                    {{ errors.first("card_expiry") }}
                  </div>
                </div>
                <div class="sm:w-full py-4">
                  <label class="font-medium"> CCV </label>
                  <div class="w-full">
                    <input
                      tabindex="4"
                      v-validate="`${billing.account_number ? 'required' : ''}`"
                      name="ccv"
                      type="number"
                      v-model="billing.ccv"
                      class="sm:w-48 w-full border py-2 px-3 mt-2 rounded"
                      :class="{ 'border-red': errors.has('ccv') }"
                    />
                  </div>
                  <div v-show="errors.has('ccv')" class="text-red text-sm mt-2">
                    {{ errors.first("ccv") }}
                  </div>
                </div>
              </div>
              <card-storage-consent></card-storage-consent>
            </div>

            <!-- direct-debit -->
            <div
              v-if="billing.type == 'direct-debit'"
              class="w-full flex flex-col sm:flex-row"
            >
              <div class="sm:w-1/2 sm:pr-8 w-full py-4">
                <label class="font-medium"> Account Name </label>
                <input
                  tabindex="1"
                  type="text"
                  class="w-full border py-2 px-3 mt-2 rounded"
                  v-model="billing.account_name"
                  v-validate="'max:32|min:3'"
                  key="account_name"
                  name="account_name"
                  :class="{ 'border-red': errors.has('account_name') }"
                />
                <div
                  v-show="errors.has('account_name')"
                  class="text-red text-sm mt-2"
                >
                  {{ errors.first("account_name") }}
                </div>
              </div>
              <div class="sm:w-1/2 w-full py-4">
                <label class="font-medium"> Account Number </label>
                <input
                  tabindex="2"
                  type="text"
                  class="w-full border py-2 px-3 mt-2 rounded"
                  v-model="billing.account_number"
                  v-validate="
                    `${
                      billing.account_name ? 'required' : ''
                    }|numeric|min:4|max:10`
                  "
                  key="account_number"
                  name="account_number"
                  :class="{ 'border-red': errors.has('account_number') }"
                />
                <div
                  v-show="errors.has('account_number')"
                  class="text-red text-sm mt-2"
                >
                  {{ errors.first("account_number") }}
                </div>
              </div>
            </div>
          </div>

          <div class="flex flex-wrap" v-if="billing.type == 'direct-debit'">
            <div class="w-1/2 pr-8 py-4">
              <label class="font-medium"> BSB </label>
              <input
                tabindex="3"
                type="text"
                class="w-full border py-2 px-3 mt-2 rounded"
                v-model="billing.bsb"
                v-validate="
                  `${billing.account_number ? 'required' : ''}|digits:6`
                "
                name="bsb"
                :class="{ 'border-red': errors.has('bsb') }"
              />
              <div v-show="errors.has('bsb')" class="text-red text-sm mt-2">
                {{ errors.first("bsb") }}
              </div>
            </div>
          </div>

          <div class="flex flex-wrap mt-4">
            <div class="w-full pb-4">
              <label class="font-medium text-center sm:text-left">
                <span class="text-red">*</span> Email Address for Invoice
              </label>
              <input
                tabindex="10"
                type="email"
                class="w-full border py-2 px-3 mt-2 rounded"
                v-validate="'required'"
                v-model="invoiceDetails.email"
                name="email"
                :class="{ 'border-red': errors.has('email') }"
              />
              <div v-show="errors.has('email')" class="text-red text-sm mt-2">
                {{ errors.first("email") }}
              </div>
            </div>
          </div>

          <div class="text-sm mt-4">
            I / We authorise Payrix Australia Pty Ltd ABN 63 135 196 397, User
            ID 382220, to debit payments from my/our account as specified above,
            at intervals and amount as directed by StrataVault (“The Business”)
            as per the Term and Conditions of my agreement with StrataVault in
            accordance with this Direct Debit Request and the Payrix DDR Service
            Agreement provided
            <a href="/DDR-Service-Agreement.pdf" target="_blank">here</a>.
          </div>

          <div class="flex justify-between mt-6">
            <div class="self-center">
              <p class="font-bold">StrataVault</p>
              <div class="text-sm">
                <p class="mt-2">Sydney, NSW</p>
                <p class="mt-2">Australia</p>
              </div>
              <div class="text-sm mt-4">
                <a
                  href="tel:1300082858"
                  target="_blank"
                  class="text-black block"
                >
                  1300 0 VAULT (82858)</a
                >
                <a
                  href="mailto:team@thestratavault.com"
                  target="_blank"
                  class="text-black block mt-1"
                >
                  team@thestratavault.com</a
                >
                <a
                  href="https://www.linkedin.com/company/strata-vault-pty-ltd/"
                  target="_blank"
                  class="text-black block mt-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="#0077B5"
                  >
                    <path
                      d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                    />
                  </svg>
                </a>
              </div>
              <div class="mt-2 italic">
                All payment information is encrypted using SSL.
              </div>
            </div>
            <!-- submit button -->
            <button
              tabindex="11"
              type="submit"
              class="
                px-6
                py-3
                bg-brand
                text-white
                rounded
                font-medium
                hover:bg-brand-dark
                self-start
              "
            >
              <i class="fa fa-save mr-2"></i> Update
            </button>
          </div>
        </div>
      </form>

      <div
        class="bg-orange-lightest text-orange-dark p-4 text-center w-full"
        role="alert"
        v-else-if="!vaultHasNoSubscription"
      >
        <p>
          Unable to update subscription details because you do not have any
          subscriptions or you are unauthorized.
        </p>
      </div>
    </div>

    <div
      v-else
      class="w-full h-screen flex flex-col justify-center items-center"
    >
      <i class="fa fa-circle-notch fa-spin fa-3x"></i>
      <p class="mt-4">Please wait...</p>
    </div>

    <sweet-modal ref="processingModal" overlay-theme="dark" hide-close-button>
      <div class="mt-4">
        <p class="text-6xl text-grey-dark">
          <i class="fa fa-spin fa-circle-notch"></i>
        </p>
        Please wait...
      </div>
    </sweet-modal>

    <sweet-modal icon="error" ref="errorModal" overlay-theme="dark">
      <p class="font-medium">An error occurred</p>
      <p class="mt-4">{{ errorMessage }}</p>
    </sweet-modal>
  </div>
</template>

<script>
import MaskedInput from "vue-masked-input";
import { mapGetters } from "vuex";
import { tokenizeCard } from "../../payrix";
import { parseCardExpiry } from "../../card_expiry.mjs";
import * as Sentry from "@sentry/vue";
import CardStorageConsent from "../../components/CardStorageConsent.vue";

export default {
  components: { MaskedInput, CardStorageConsent },

  data() {
    return {
      authorized: false,
      checkCurrentSubscription: true,
      currentSubscription: {},
      vaultHasNoSubscription: false,
      availablePlansForLockedVault: [],
      selectedPlanForLockedVaultIndex: null,

      cardBrand: null,
      errorMessage: "",

      billing: {
        type: "credit-card",
        account_name: "",
        account_number: "",
        card_expiry: "",
        ccv: "",
        bsb: "",
        card_token: "",
      },
      invoiceDetails: {},

      states: [
        "Australian Capital Territory",
        "New South Wales",
        "Northern Territory",
        "Queensland",
        "South Australia",
        "Tasmania",
        "Victoria",
        "Western Australia",
      ],

      types: [
        {
          code: "credit-card",
          name: "Credit Card",
        },
        {
          code: "direct-debit",
          name: "Direct Debit",
        },
      ],
    };
  },

  computed: {
    ...mapGetters({ plans: "getPlans" }),
    firstAvailablePlan() {
      const plans = this.plans || {};
      const entries = Object.entries(plans);
      if (!entries.length) return null;
      const [key, plan] = entries[0];
      return { key, name: plan.name, interval: plan.interval };
    },
  },

  mounted() {
    this.getCurrentSubscription();
  },

  methods: {
    getCurrentSubscription() {
      axios.get(`api/users/subscriptions`).then(({ data }) => {
        this.checkCurrentSubscription = false;

        let vault_id = this.$route.params.vault_id;

        if (data && data.length > 0) {
          this.currentSubscription = _.find(data, function (subscription) {
            if(subscription.vault && subscription.vault.id) {
              return subscription.vault.id == vault_id;
            }
          });

          if (!_.isEmpty(this.currentSubscription)) {
            this.authorized = true;
            this.invoiceDetails =
              this.currentSubscription.vault.invoice_details;
          } else if (vault_id) {
            this.vaultHasNoSubscription = true;
            this.fetchAvailablePlansForLockedVault();
          }
        } else if (vault_id) {
          this.vaultHasNoSubscription = true;
          this.fetchAvailablePlansForLockedVault();
        }
      });
    },
    fetchAvailablePlansForLockedVault() {
      const vault_id = this.$route.params.vault_id;
      if (!vault_id) return;
      axios
        .get("api/users/subscriptions/available-plans", {
          params: { vault_id },
        })
        .then(({ data }) => {
          const list = data || [];
          this.availablePlansForLockedVault = list;
          if (list.length && (this.selectedPlanForLockedVaultIndex == null || this.selectedPlanForLockedVaultIndex >= list.length)) {
            this.selectedPlanForLockedVaultIndex = 0;
          }
        });
    },
    async tokenize(billing) {
      const response = await axios.get(`api/payrix/business-details`);
      const { data } = await response.data;

      const { month, year } = parseCardExpiry(billing.card_expiry);
      const card = {
        CardholderName: billing.account_name,
        CardNumber: billing.account_number,
        Ccv: billing.ccv,
        ExpiryMonth: month,
        ExpiryYear: year,
      };

      const payrixUrl = process.env.MIX_PAYRIX_URL;
      return await tokenizeCard(payrixUrl, data.businessKey, {
        ...card,
      });
    },
    async submitBilling() {
      if (this.vaultHasNoSubscription) {
        this.$refs.processingModal.open();
        await this.submitCreateSubscription();
        return;
      }

      const result = await this.$validator.validateAll();

      if (result) {
        this.$refs.processingModal.open();

        // update billing details
        if (
          this.billing.card_token != "" ||
          this.billing.account_name != "" ||
          this.billing.account_number != "" ||
          this.billing.bsb
        ) {
          try {
            // check if billing type is credit-card or debit
            // if billing type is credit-card
            // then tokenize the payment details
            if (this.billing.type == "credit-card") {
              try {
                const billingCopy = { ...this.billing };

                this.billing.account_name = "";
                this.billing.account_number = "";
                this.billing.card_expiry = "";
                this.billing.ccv = "";
                this.billing.bsb = "";

                const response = await this.tokenize(billingCopy);
                const {
                  account_number,
                  account_name,
                  card_expiry,
                  ccv,
                  bsb,
                  ...billing
                } = this.billing;

                this.billing = {
                  ...billing,
                  card_token: response.token,
                };
              } catch (error) {
                Sentry.captureException(error);

                this.errorMessage = error.response.data.errorMessage;
                this.$refs.processingModal.close();
                this.$refs.errorModal.open();
                return;
              }
            } else {
              const { ccv, card_expiry, ...billing } = this.billing;
              this.billing = { ...billing };
            }

            const response = await axios.put(
              `api/users/subscriptions/${this.currentSubscription.id}/billing`,
              {
                ...this.billing,
              }
            );

            const { success } = await response.data;
            if (success) {
              this.$refs.processingModal.close();
              this.$noty.success(`Payment details updated successfully.`);
              this.billing = {
                ...this.billing,
                account_name: "",
                account_number: "",
                card_expiry: "",
                ccv: "",
                bsb: "",
                card_token: "",
              };

              this.$nextTick(() => {
                this.errors.clear();
                this.$nextTick(() => {
                  this.$validator.reset();
                });
              });
            } else {
              this.errorMessage = error.response.data.message;
              this.$refs.processingModal.close();
              this.$refs.errorModal.open();
            }
          } catch (error) {
            Sentry.captureException(error);

            this.errorMessage =
              "Update Billing: " + error.response.data.message;
            this.$refs.processingModal.close();
            this.$refs.errorModal.open();
          }
        }

        // update invoice details
        try {
          const { address, country, city, state, postcode, email } =
            this.invoiceDetails;
          const response = await axios.put(
            `api/vaults/${this.$route.params.vault_id}/invoice-details`,
            {
              address,
              country,
              city,
              state,
              postcode,
              email,
            }
          );

          const { success } = await response.data;
          if (success) {
            this.$noty.success(`Invoice details updated successfully.`);
          } else {
            this.$noty.error(`Failed to update invoice details.`);
          }
          this.$refs.processingModal.close();
        } catch (error) {
          Sentry.captureException(error);

          this.errorMessage =
            "Update Invoice Details: " + error.response.data.message;

          this.$noty.error(error.response.data.message);
          this.$refs.processingModal.close();
        }
      }
    },
    async submitCreateSubscription() {
      try {
        if (this.billing.type === "credit-card") {
          if (!this.billing.account_name || !this.billing.account_number || !this.billing.card_expiry || !this.billing.ccv) {
            this.errorMessage = "Please fill in cardholder name, card number, expiry and CCV.";
            this.$refs.processingModal.close();
            this.$refs.errorModal.open();
            return;
          }
        } else {
          if (!this.billing.account_name || !this.billing.account_number || !this.billing.bsb) {
            this.errorMessage = "Please fill in account name, account number and BSB.";
            this.$refs.processingModal.close();
            this.$refs.errorModal.open();
            return;
          }
        }

        const list = this.availablePlansForLockedVault || [];
        if (!list.length) {
          this.errorMessage = "No plans available. Please contact support.";
          this.$refs.processingModal.close();
          this.$refs.errorModal.open();
          return;
        }
        const idx = this.selectedPlanForLockedVaultIndex != null ? parseInt(this.selectedPlanForLockedVaultIndex, 10) : -1;
        const selectedPlan = list[idx];
        if (!selectedPlan || idx < 0) {
          this.errorMessage = "Please select a plan.";
          this.$refs.processingModal.close();
          this.$refs.errorModal.open();
          return;
        }
        const plan = { name: selectedPlan.name, interval: selectedPlan.interval };

        const payload = {
          plan: plan.name,
          plan_cycle: plan.interval,
          billing: {
            type: this.billing.type,
            account_name: this.billing.account_name,
            account_number: this.billing.account_number,
            bsb: this.billing.bsb,
          },
          billing_invoice: this.invoiceDetails && Object.keys(this.invoiceDetails).length ? this.invoiceDetails : {},
        };

        if (this.billing.type === "credit-card") {
          const billingCopy = { ...this.billing };
          const tokenResponse = await this.tokenize(billingCopy);
          payload.billing.card_token = tokenResponse.token;
        }

        const response = await axios.post(
          `api/vaults/${this.$route.params.vault_id}/create-subscription`,
          payload
        );

        const res = response.data;
        if (res.success) {
          this.$refs.processingModal.close();
          if (this.$noty) this.$noty.success(res.message || "Vault unlocked successfully.");
          this.$ebus.$emit("refresh-vaults");
          this.$router.push({ name: "vaults" });
        } else {
          this.errorMessage = res.message || "Failed to create subscription.";
          this.$refs.processingModal.close();
          this.$refs.errorModal.open();
        }
      } catch (error) {
        Sentry.captureException(error);
        this.errorMessage =
          (error.response && error.response.data && error.response.data.message) || error.message || "Failed to unlock vault.";
        this.$refs.processingModal.close();
        this.$refs.errorModal.open();
        if (this.$noty) this.$noty.error(this.errorMessage);
      }
    },
  },
};
</script>

<style>
.multiselect__tags {
  min-height: 0px;
  display: block;
  padding: 6px 40px 0 8px;
  border-radius: 5px;
  border: 1px solid #dae4e9;
  background: #fff;
}
/* .invalid > .multiselect__tags {
  border: 1px solid #e3342f;
} */
.multiselect__input {
  font-size: 16px;
}
</style>
